import type { PlanDraft } from "@weights/plan-reading";

/** One value in the draft, before and after: an undo step is a list of these (grid doc §4). */
export interface Change {
  path: (string | number)[];
  before: unknown;
  after: unknown;
}

export function getIn(root: unknown, path: (string | number)[]): unknown {
  let node: unknown = root;
  for (const key of path) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string | number, unknown>)[key];
  }
  return node;
}

function setInNode(node: unknown, path: (string | number)[], value: unknown): unknown {
  const [key, ...rest] = path;
  if (key === undefined) return value;
  if (Array.isArray(node)) {
    const copy = node.slice();
    copy[key as number] = setInNode(copy[key as number], rest, value);
    return copy;
  }
  const obj = { ...((node ?? {}) as Record<string, unknown>) };
  const next = setInNode(obj[key as string], rest, value);
  if (next === undefined) delete obj[key as string];
  else obj[key as string] = next;
  return obj;
}

/** `undefined` removes an object key, so an emptied cell leaves no trace in the JSON. */
export function setIn<T>(root: T, path: (string | number)[], value: unknown): T {
  return setInNode(root, path, value) as T;
}

export function applyChanges(draft: PlanDraft, changes: Change[], direction: "forward" | "back" = "forward"): PlanDraft {
  const list = direction === "forward" ? changes : [...changes].reverse();
  let out = draft;
  for (const change of list) out = setIn(out, change.path, direction === "forward" ? change.after : change.before);
  return out;
}

export function change(draft: PlanDraft, path: (string | number)[], after: unknown): Change {
  return { path, before: clone(getIn(draft, path)), after: clone(after) };
}

function clone<T>(value: T): T {
  return value === undefined ? value : structuredClone(value);
}

export function isNoop(c: Change): boolean {
  return JSON.stringify(c.before) === JSON.stringify(c.after);
}
